import { rngInt } from '../math/rng';
import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameEventDraft, type GameRuntime, type GameState } from '../engine/state';
import { enterPhase, nextActiveSeat, openWindow } from '../engine/phase';
import { finalizeGame } from './victory';

/** The classic end-of-turn rotation (plan §7.6) — shared with the turn-mode modules,
 *  which replace END_TURN's registration but not the rotation itself. */
export function rotateTurn(state: GameState, env: GameRuntime, emit: (e: GameEventDraft) => void): void {
  for (const { hook } of env.composed.turnBoundary) hook(state, env); // offers clear etc. (plan §5.2)
  state.turn.number += 1;
  state.turn.roller = nextActiveSeat(state, state.turn.roller);
  emit({ kind: 'TURN_STARTED', vis: 'public', seat: state.turn.roller, turnNumber: state.turn.number });
  enterPhase(state, env, { t: 'PRE_ROLL', seat: state.turn.roller }, emit);
  openWindow(state, env, state.turn.roller); // a FRESH window — allowances reset here only
}

// 'turn-flow' — the turn skeleton (plan §7.3/§7.6; D11): PRE_ROLL/ACTION windows, ROLL
// (dice draws + the onDiceResolved chain), END_TURN rotation with turnBoundary hooks,
// RESIGN. Paired/SBP override the END_TURN transition in their own modules (H1-S11).
// Dice: two draws, die1 then die2 — plan §9.3 normative order.

export const turnFlowModule: RuleModule = {
  id: 'turn-flow',
  register(ctx) {
    ctx.phaseState('PRE_ROLL', { eligibleWinner: (n) => (n.t === 'PRE_ROLL' ? n.seat : null), timeoutKey: 'PRE_ROLL' });
    ctx.phaseState('ACTION', { eligibleWinner: (n) => (n.t === 'ACTION' ? n.seat : null), timeoutKey: 'ACTION' });
    ctx.phaseState('GAME_OVER', { eligibleWinner: () => null, timeoutKey: 'GAME_OVER' });

    ctx.action('ROLL', {
      enumerate(state, _env, seat) {
        return state.phase.t === 'PRE_ROLL' && state.phase.seat === seat ? [{}] : null;
      },
      validate(state, action) {
        if (state.phase.t !== 'PRE_ROLL') return reject('WRONG_PHASE');
        if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
        return OK;
      },
      apply(state, action, env, emit) {
        const holder = { rng: state.rng };
        const d1 = rngInt(holder, 6) + 1; // plan §9.3 draw 1
        const d2 = rngInt(holder, 6) + 1; // plan §9.3 draw 2
        state.rng = holder.rng;
        emit({ kind: 'DICE_ROLLED', vis: 'public', seat: action.seat, d1, d2 });
        for (const { handle } of env.composed.diceHandlers) {
          if (handle(state, env, d1, d2, emit) === 'handled') return; // the handler owns the transition (plan §5.3)
        }
        enterPhase(state, env, { t: 'ACTION', seat: action.seat }, emit); // default: straight to the action window
      },
    });

    ctx.action('END_TURN', {
      enumerate(state, _env, seat) {
        return state.phase.t === 'ACTION' && state.phase.seat === seat ? [{}] : null;
      },
      validate(state, action) {
        // paired/SBP widen this window by REPLACING the registration (ordered override)
        if (state.phase.t !== 'ACTION') return reject('WRONG_PHASE');
        if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
        return OK;
      },
      apply(state, _action, env, emit) {
        rotateTurn(state, env, emit);
      },
    });

    ctx.action('RESIGN', {
      enumerate(state, _env, seat) {
        return state.seats[seat] && !state.seats[seat].resigned ? [{}] : null;
      },
      validate(state, action) {
        const seat = state.seats[action.seat];
        if (!seat) return reject('BAD_SEAT');
        if (seat.resigned) return reject('ALREADY_RESIGNED');
        return OK; // legal in any phase but GAME_OVER (envelope rejects that)
      },
      apply(state, action, _env, emit) {
        state.seats[action.seat]!.resigned = true;
        state.seats[action.seat]!.resignedAtSeq = state.seq;
        emit({ kind: 'PLAYER_RESIGNED', vis: 'public', seat: action.seat });
        // the seat's pending windows resolve via the vacancy policy (plan §13.5, D5) —
        // no auto-advance here; rotation skips resigned seats from the next END_TURN on
      },
    });

    // administrative early finalize (plan §13.5, PT-6): the ONE non-victory ending.
    // Engine-side it is an ordinary logged action; the WIRE refuses client-sent copies
    // (T-SEC-001) — origin policy is the server's, not the engine's.
    ctx.action('SYSTEM_END', {
      enumerate() {
        return null; // never offered to players
      },
      validate(_state, action, env) {
        const w = action.payload['winner'];
        if (typeof w !== 'number' || !Number.isInteger(w) || w < 0 || w >= env.seatsCount) return reject('BAD_PAYLOAD');
        if (action.payload['reason'] !== 'LAST_HUMAN') return reject('BAD_PAYLOAD');
        return OK;
      },
      apply(state, action, env, emit) {
        finalizeGame(state, env, Number(action.payload['winner']), emit);
      },
    });
  },
};
